import { useMemo, useState } from 'react';
import { blockRegistry, type BlockType } from '@sarnia/shared';
import type { IconName } from '../components/ui/Icon';
import { Icon } from '../components/ui/Icon';
import { editorStore } from './editorStore';

const categories: Array<'content' | 'media' | 'contact' | 'layout'> = [
  'content',
  'media',
  'contact',
  'layout'
];

const blockIcons: Partial<Record<BlockType, IconName>> = {
  header: 'layout',
  hero: 'sparkles',
  text: 'type',
  imageText: 'image',
  featureGrid: 'grid',
  stats: 'layers',
  steps: 'move',
  quote: 'type',
  banner: 'zap',
  gallery: 'image',
  pricing: 'star',
  testimonials: 'star',
  menuList: 'book',
  openingHours: 'clock',
  contact: 'mail',
  mapAddress: 'globe',
  faq: 'help',
  cta: 'zap',
  socialLinks: 'link',
  footer: 'layers',
  spacer: 'move',
  designed: 'plus'
};

export default function BlockPalette() {
  const [query, setQuery] = useState('');
  const normalizedQuery = query.trim().toLowerCase();

  const visibleBlocks = useMemo(() => {
    return Object.values(blockRegistry).filter((definition) => {
      if (definition.type === 'designed') {
        return false;
      }
      if (!normalizedQuery) {
        return true;
      }
      return definition.label.toLowerCase().includes(normalizedQuery);
    });
  }, [normalizedQuery]);

  const blocksByCategory = categories
    .map((category) => ({
      category,
      blocks: visibleBlocks.filter((definition) => definition.category === category)
    }))
    .filter((group) => group.blocks.length > 0);

  let cardIndex = 0;

  return (
    <div className="block-palette">
      <div className="block-palette-search">
        <Icon name="search" size={15} className="block-palette-search-icon" />
        <input
          type="search"
          className="block-palette-search-input"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search blocks"
          aria-label="Search blocks"
        />
      </div>

      {blocksByCategory.length === 0 ? (
        <div className="block-palette-empty">
          {normalizedQuery ? `No blocks match “${query}”` : 'No addable blocks.'}
        </div>
      ) : (
        blocksByCategory.map(({ category, blocks }) => (
          <section className="block-palette-group" key={category}>
            <h3 className="block-palette-title">{category}</h3>
            <div className="block-palette-grid">
              {blocks.map((definition) => {
                const isFirstCard = cardIndex === 0;
                cardIndex += 1;
                return (
                  <button
                    key={definition.type}
                    type="button"
                    className="block-palette-item"
                    onClick={() => editorStore.addBlock(definition.type)}
                    data-tour={isFirstCard ? 'block-card' : undefined}
                    title={definition.label}
                  >
                    <span className="block-palette-icon" aria-hidden="true">
                      <Icon name={blockIcons[definition.type] ?? 'plus'} size={17} />
                    </span>
                    <span className="block-palette-label">{definition.label}</span>
                  </button>
                );
              })}
            </div>
          </section>
        ))
      )}
    </div>
  );
}
